export const defaultAnalysisSettings = {
  symbol_a: 'BTCUSDT',
  timeframe: '5',
  zscore_period: '300',
  bar: '10000',
  margin: '100',
  leverage: '1',
  commission_rate: '0.1',
  exit: '0',
  shortLevels: '2, 3, 4',
  longLevels: '-2, -3, -4',
  dateFilterEnabled: false,
  dateFrom: '2025-10-09',
  dateTo: '2025-10-11'
};

const toNumber = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const formatNumericString = (value, fallback = '0') => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return fallback;
  return String(parsed);
};

export const parseLevelsString = (value) =>
  String(value || '')
    .split(',')
    .map((part) => parseFloat(part.trim()))
    .filter((number) => !Number.isNaN(number));

const formatLevels = (levels, fallback) =>
  Array.isArray(levels) && levels.length ? levels.join(', ') : fallback;

export const formatDateForBackend = (isoDate) => {
  if (!isoDate) return null;
  const [year, month, day] = String(isoDate).split('-');
  if (!year || !month || !day) return null;
  return `${day}.${month}.${year}`;
};

const parseDateFromBackend = (backendDate, fallback) => {
  if (!backendDate) return fallback;
  const [day, month, year] = String(backendDate).split('.');
  if (!day || !month || !year) return fallback;
  return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
};

export const buildAnalyzePayload = (settings) => {
  const margin = toNumber(settings?.margin);
  const leverage = toNumber(settings?.leverage);

  return {
    symbol_a: String(settings?.symbol_a || '').trim().toUpperCase(),
    timeframe: toNumber(settings?.timeframe, 5),
    zscore_period: toNumber(settings?.zscore_period, 300),
    bar: toNumber(settings?.bar, 10000),
    strategy_method: 'method1',
    strategy_name: 'zscore',
    total_usdt_one_entry: margin * leverage,
    commission_rate: (parseFloat(String(settings?.commission_rate ?? '0').replace(',', '.')) || 0) / 100,
    exit: toNumber(settings?.exit, 0),
    with_date: settings?.dateFilterEnabled ? formatDateForBackend(settings?.dateFrom) : null,
    by_date: settings?.dateFilterEnabled ? formatDateForBackend(settings?.dateTo) : null,
    level_entry_positive: parseLevelsString(settings?.shortLevels),
    level_entry_negative: parseLevelsString(settings?.longLevels),
    only_usdt: true
  };
};

export const buildTemplatePayload = (templateName, settings) => ({
  templateName: String(templateName || '').trim(),
  analyzeRequest: {
    ...buildAnalyzePayload(settings),
    margin: toNumber(settings?.margin),
    leverage: toNumber(settings?.leverage)
  }
});

export const mapAnalyzeRequestToSettings = (request = {}) => {
  const normalizedRequest = request?.analyzeRequest && typeof request.analyzeRequest === 'object'
    ? request.analyzeRequest
    : request;

  const totalUsdtOneEntry = toNumber(normalizedRequest?.total_usdt_one_entry);
  const rawMargin = toNumber(normalizedRequest?.margin);
  const rawLeverage = toNumber(normalizedRequest?.leverage);

  let margin = rawMargin;
  let leverage = rawLeverage;

  if (!margin && totalUsdtOneEntry && leverage) {
    margin = totalUsdtOneEntry / leverage;
  }

  if (!leverage && totalUsdtOneEntry && margin) {
    leverage = totalUsdtOneEntry / margin;
  }

  if (!margin && totalUsdtOneEntry) {
    margin = totalUsdtOneEntry;
  }

  if (!leverage) {
    leverage = 1;
  }

  return {
    ...defaultAnalysisSettings,
    symbol_a: String(normalizedRequest?.symbol_a || defaultAnalysisSettings.symbol_a).toUpperCase(),
    timeframe: formatNumericString(normalizedRequest?.timeframe, defaultAnalysisSettings.timeframe),
    zscore_period: formatNumericString(normalizedRequest?.zscore_period, defaultAnalysisSettings.zscore_period),
    bar: formatNumericString(normalizedRequest?.bar, defaultAnalysisSettings.bar),
    margin: formatNumericString(margin, defaultAnalysisSettings.margin),
    leverage: formatNumericString(leverage, defaultAnalysisSettings.leverage),
    commission_rate: formatNumericString(toNumber(normalizedRequest?.commission_rate) * 100, defaultAnalysisSettings.commission_rate),
    exit: formatNumericString(normalizedRequest?.exit, defaultAnalysisSettings.exit),
    shortLevels: formatLevels(normalizedRequest?.level_entry_positive, defaultAnalysisSettings.shortLevels),
    longLevels: formatLevels(normalizedRequest?.level_entry_negative, defaultAnalysisSettings.longLevels),
    dateFilterEnabled: Boolean(normalizedRequest?.with_date || normalizedRequest?.by_date),
    dateFrom: parseDateFromBackend(normalizedRequest?.with_date, defaultAnalysisSettings.dateFrom),
    dateTo: parseDateFromBackend(normalizedRequest?.by_date, defaultAnalysisSettings.dateTo)
  };
};
